import objectPath from 'object-path';

import { routerAsync } from '@/app-module';
import { i18n } from '@shared/i18n';

import Errors from '@shared/error/errors';
import Message from '@shared/message/message';

import { StripeExternalAccountService } from '@modules/stripe/stripe-external-account/stripe-external-account-service';
import { StripeConnectedAccountService } from '@modules/stripe/stripe-connected-account/stripe-connected-account-service';

const defaultRecord = {};

export default {
  namespaced: true,

  state: {
    findLoading: false,
    saveLoading: false,
    newLoading: false,
    record: defaultRecord,
    globalErrorMessage: null,
    fieldErrors: {},
    country: null,
    currency: null,
    isFirstMethod: false,
  },

  getters: {
    record: (state) => {
      const country = state.country || 'US'
      const currency = state.currency || 'usd'
      const isFirstMethod = state.isFirstMethod
        
      return {
        ...state.record,
        country,
        currency,
        isFirstMethod,
        defaultForCurrency: isFirstMethod,
        connectedAccountCountry: country,
      }
    },
    globalErrorMessage: (state) => state.globalErrorMessage,
    fieldErrors: (state) => state.fieldErrors,
    findLoading: (state) => !!state.findLoading,
    saveLoading: (state) => !!state.saveLoading,
    newLoading: (state) => !!state.newLoading,
  },

  mutations: {
    RESET(state) {
      state.findLoading = false;
      state.saveLoading = false;
      state.record = defaultRecord;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    NEW_STARTED(state) {
      state.newLoading = true;
    },

    NEW_SUCCESS(state, { country, currency, isFirstMethod }) {
      state.country = country;
      state.currency = currency;
      state.isFirstMethod = isFirstMethod;
      state.newLoading = false;
    },

    NEW_ERROR(state) {
      state.newLoading = false;
    },

    FIND_STARTED(state) {
      state.record = defaultRecord;
      state.findLoading = true;
    },

    FIND_SUCCESS(state, payload) {
      state.record = payload;
      state.findLoading = false;
    },

    FIND_ERROR(state) {
      state.record = defaultRecord;
      state.findLoading = false;
    },

    CREATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    CREATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
    },

    SET_ERROR(state, { errorMessage, errors }) {
      state.saveLoading = false;

      state.globalErrorMessage = null;
      state.fieldErrors = [];

      state.globalErrorMessage = errorMessage;
      state.fieldErrors = errors;
    },

    CLEAR_FIELD_ERRORS(state, { fieldNames }) {
      for (const fieldName of fieldNames) {
        objectPath.del(state.fieldErrors, fieldName);
      }
      state.fieldErrors = Object.assign({}, state.fieldErrors);
      if (!Object.keys(state.fieldErrors).length) state.globalErrorMessage = null;
    }
  },

  actions: {
    async doNew({ commit, rootGetters }, { propertyId, connectedAccountId }) {
      try {
        commit('RESET');
        commit('NEW_STARTED');

        const currentManage = rootGetters['auth/manage/currentManage']
        const manageConnectedAccountId = 'subproperty' in currentManage && currentManage.subproperty.connectedAccountId

        if (!manageConnectedAccountId) {
          return routerAsync().push({ name: 'home' });
        } else if (connectedAccountId !== manageConnectedAccountId) {
          routerAsync().push({
            params: {
              connectedAccountId: manageConnectedAccountId,
            },
          });
        }

        const fetchId = connectedAccountId !== manageConnectedAccountId ? manageConnectedAccountId : connectedAccountId;

        const {
          defaultCurrency: currency,
          address: { country }
        } = await StripeConnectedAccountService.find(fetchId, propertyId);

        const { count } = await StripeExternalAccountService.list(propertyId, fetchId);

        commit('NEW_SUCCESS', { currency, country, isFirstMethod: count === 0 });
      } catch (error) {
        Errors.handle(error);
        commit('NEW_ERROR');
        routerAsync().push('/');
      }
    },

    async doFind({ commit }, { id, propertyId, connectedAccountId }) {
      try {
        commit('FIND_STARTED');
        const record = await StripeExternalAccountService.find(id, propertyId, connectedAccountId);
        commit('FIND_SUCCESS', record);
      } catch (error) {
        Errors.handle(error);
        commit('FIND_ERROR');
        routerAsync().push('/');
      }
    },

    async doCreate({ commit, dispatch, rootGetters }, { propertyId, subpropertyId, externalAccountToken, defaultForCurrency, backToSettings = false }) {
      try {
        commit('CREATE_STARTED');
        await StripeExternalAccountService.create({ propertyId, subpropertyId, externalAccountToken, defaultForCurrency });
        commit('CREATE_SUCCESS');

        const currentManage = rootGetters['auth/manage/currentManage'];
        dispatch('subproperty/requirements/doFetch', {
          propertyId: currentManage.property.id,
          subpropertyId: currentManage.subproperty.id,
          userId: rootGetters['auth/currentUser'].id,
        }, {
          root: true,
        });

        Message.success(i18n('entities.stripeExternalAccount.create.success'));

        const goTo = backToSettings
          ? { name: 'finantialSettings', params: { propertyId, subpropertyId, tabType: 'payoutMethods' } }
          : { name: 'home' };
        routerAsync().push(goTo);
      } catch (error) {
        Errors.handle(error, (errorMessage, errors) => {
          commit('SET_ERROR', { errorMessage, errors });
        });
      }
    },

    doHandleStripeError({ commit }, error) {
      const wrappedError = {};
      objectPath.set(wrappedError, 'networkError.result.errors.0.code', 400);
      objectPath.set(wrappedError, 'networkError.result.errors.0.raw', error);
      objectPath.set(wrappedError, 'networkError.result.errors.0.fromStripe', true);

      Errors.handle(wrappedError, (errorMessage, errors) => {
        commit('SET_ERROR', { errorMessage, errors });
      });
    },

    doSetStripeError({ commit }, { message }) {
      commit('SET_ERROR', { errorMessage: message });
    },

    doClearStripeError({ commit }, { fieldNames }) {
      commit('CLEAR_FIELD_ERRORS', { fieldNames });
    },
  },
};
